import React, { useState } from 'react';
import Text from '@service-victoria/component-library/components/Text';
import TextArea from '@service-victoria/component-library/components/TextArea';
import * as Styled from 'steps/styled';
import FeedbackItem from './FeedbackItem';
import ThankYou from './ThankYou';
import { FeedbackWrapper, IconsWrapper, ButtonWrapper } from './styled';
import { Formik } from 'formik';
import { trackEvent } from 'utils/analytics';

type SelectionTypes = 'up' | 'down';

export type ComponentProps = {
  onThumbSelect?: (type: SelectionTypes) => void;
  onFeedbackGiven?: (type: SelectionTypes, feedback: string) => void;
  allowTextFeedback?: boolean;
};

const CompletionSurvey: React.FC<ComponentProps> = ({
  onThumbSelect,
  onFeedbackGiven,
  allowTextFeedback = false
}) => {
  const [feedback, setFeedback] = useState<{ isGivenFeedback: boolean; type?: SelectionTypes }>({
    isGivenFeedback: false
  });
  const [sent, setSent] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  const handleClick = (type: SelectionTypes) => {
    if (!feedback.isGivenFeedback) {
      setFeedback({
        isGivenFeedback: true,
        type
      });

      if (onThumbSelect) {
        onThumbSelect(type);
      }
    }

    // scroll the input form into view 10ms delay
    setTimeout(() => {
      ref?.current?.scrollIntoView({
        behavior: 'smooth'
      });
    }, 10);
  };

  return sent ? (
    <ThankYou text='Thank you' />
  ) : (
    <Formik
      initialValues={{ feedback: '' }}
      onSubmit={values => {
        setSent(true);
        if (onFeedbackGiven && feedback.type) {
          onFeedbackGiven(feedback.type, values.feedback);
        }
      }}>
      {({ handleSubmit }) => (
        <div>
          <Text.Heading.H3 fontWeight={600} style={{ marginBottom: '15px' }}>
            How was your experience?
          </Text.Heading.H3>
          <FeedbackWrapper>
            <IconsWrapper>
              <FeedbackItem
                type='up'
                onClick={() => handleClick('up')}
                disabled={feedback.isGivenFeedback && feedback.type !== 'up'}
                selected={feedback.type === 'up'}
                feedbackText='Good'
                data-testid='feedback-thumbs-up'
              />

              <FeedbackItem
                type='down'
                onClick={() => handleClick('down')}
                disabled={feedback.isGivenFeedback && feedback.type !== 'down'}
                selected={feedback.type === 'down'}
                feedbackText='Bad'
                data-testid='feedback-thumbs-down'
              />
            </IconsWrapper>
            {feedback.isGivenFeedback && !allowTextFeedback && (
              <>
                <Styled.Spacer16 />
                <ThankYou text='Thank you' />
              </>
            )}
          </FeedbackWrapper>
          {feedback.isGivenFeedback && allowTextFeedback && (
            <>
              <Styled.Spacer space={30} />
              <TextArea
                name='feedback'
                label='Tell us why (Optional)'
                placeholder="Don't include any confidential or personal info in your feedback. (No names, phone numbers or emails.)"
                maxLength={300}
                data-testid='feedback-text'
              />
              <Text.Link
                target='_blank'
                value='Privacy statement'
                href='https://service.vic.gov.au/privacy-and-security'
                data-testid='feedback-privacy-link'
              />
              <Styled.Spacer space={24} />
              <ButtonWrapper
                buttonType='primary'
                type='button'
                data-testid='feedback-send'
                onClick={() => {
                  handleSubmit();
                  trackEvent({
                    category: 'FormCompletionFeedback',
                    action: 'covid-travel',
                    label: `submit-feedback`
                  });
                }}>
                Send feedback
              </ButtonWrapper>
              <Styled.Spacer space={24} ref={ref} />
              <ButtonWrapper
                buttonType='secondary'
                data-testid='feedback-skip'
                onClick={() => {
                  setSent(true);
                  trackEvent({
                    category: 'FormCompletionFeedback',
                    action: 'covid-travel',
                    label: 'skip-feedback'
                  });
                }}>
                Skip
              </ButtonWrapper>
            </>
          )}
        </div>
      )}
    </Formik>
  );
};

export default CompletionSurvey;
